import React, { useEffect, useMemo, useState, createElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import PartnerCreateDrawer from '../components/PartnerCreateDrawer';
import PartnerDetailDrawer from '../components/drawers/PartnerDetailDrawer';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { useAdminData } from '../contexts/AdminDataContext';
import { Building2, Search, Mail, ExternalLink, MapPin, Users, Gift, Sparkles, Eye, EyeOff, RefreshCw, Filter, Download } from 'lucide-react';
import { allCategories, aiInsights, AIInsight } from '../utils/partnersData';
// Mock AI recommendations for partners
const getPartnersRecommendations = () => [{
  id: '1',
  title: 'Espandi partnership Milano',
  type: 'Opportunità',
  diamondRange: '+35% crescita Milano',
  reason: 'Milano mostra una crescita del 35% negli utenti attivi. Considera di aggiungere 2-3 partner locali per soddisfare la domanda.',
  ctaLabel: 'Trova partner'
}, {
  id: '2',
  title: 'Partner inattivi da riattivare',
  type: 'Attenzione',
  diamondRange: '4 partner inattivi',
  reason: '4 partner sono inattivi da oltre 30 giorni. Contattali per verificare interesse a riattivare la collaborazione o aggiornare i premi.',
  ctaLabel: 'Vedi partner'
}, {
  id: '3',
  title: 'Categoria Cosmetici in crescita',
  type: 'Trend',
  diamondRange: '+28% riscatti',
  reason: 'La categoria Cosmetici Naturali ha visto un aumento del 28% nei riscatti. Valuta di aggiungere nuovi partner in questa categoria.',
  ctaLabel: 'Esplora categoria'
}];
const Partners = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    partners
  } = useAdminData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Tutte');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [globalInsights, setGlobalInsights] = useState<AIInsight[]>(aiInsights.filter(insight => !insight.partnerId || insight.partnerId === 0));
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const recommendations = getPartnersRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  useEffect(() => {
    if (location.state?.openDrawer) {
      setShowCreateDrawer(true);
      window.history.replaceState({}, document.title);
    }
    // Handle both URL params and navigation state for partner ID
    if (params.partnerId) {
      setSelectedPartnerId(params.partnerId);
      setShowDetailDrawer(true);
    } else if (location.state?.partnerId) {
      setSelectedPartnerId(location.state.partnerId);
      setShowDetailDrawer(true);
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location, params.partnerId]);
  const filteredPartners = useMemo(() => {
    return partners.filter(partner => {
      const matchesSearch = partner.name.toLowerCase().includes(searchQuery.toLowerCase()) || partner.description.toLowerCase().includes(searchQuery.toLowerCase()) || partner.categories.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'Tutte' || partner.categories.includes(selectedCategory);
      const matchesStatus = statusFilter === 'all' || partner.status === statusFilter;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [partners, searchQuery, selectedCategory, statusFilter]);
  const handleSendBulkEmail = () => {
    alert(`Email inviata a ${filteredPartners.length} partner!\n\nOggetto: ${emailSubject}\n\nMessaggio: ${emailMessage}`);
    setShowEmailModal(false);
    setEmailSubject('');
    setEmailMessage('');
  };
  const handleAddPartner = () => {
    setShowCreateDrawer(true);
  };
  const handlePartnerClick = (partnerId: string) => {
    setSelectedPartnerId(partnerId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => {
      setSelectedPartnerId(null);
      navigate('/partners', {
        replace: true
      });
    }, 300);
  };
  const handleExportCSV = () => {
    // Mock CSV export
    const csvContent = [['Nome', 'Categoria', 'Sedi', 'Premi Attivi', 'Riscatti', 'Stato'], ...filteredPartners.map(partner => [partner.name, partner.categories.join('; '), partner.locations, partner.activeRewards, partner.totalRedemptions, partner.status])].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partners-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const toggleInsightVisibility = (insightId: string) => {
    setGlobalInsights(globalInsights.map(insight => insight.id === insightId ? {
      ...insight,
      visible: !insight.visible
    } : insight));
  };
  const regenerateInsight = (insightId: string) => {
    setGlobalInsights(globalInsights.map(insight => insight.id === insightId ? {
      ...insight,
      generatedAt: new Date().toISOString(),
      description: insight.description + ' [Rigenerato]'
    } : insight));
  };
  const getInsightIcon = (type: AIInsight['type']) => {
    switch (type) {
      case 'opportunity':
        return <Sparkles className="h-5 w-5 text-green-600" data-id="element-3372" />;
      case 'warning':
        return <Sparkles className="h-5 w-5 text-yellow-600" data-id="element-3373" />;
      case 'trend':
        return <Sparkles className="h-5 w-5 text-blue-600" data-id="element-3374" />;
      case 'recommendation':
        return <Sparkles className="h-5 w-5 text-purple-600" data-id="element-3375" />;
    }
  };
  const getInsightBgColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'opportunity':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'trend':
        return 'bg-blue-50 border-blue-200';
      case 'recommendation':
        return 'bg-purple-50 border-purple-200';
    }
  };
  const totalLocations = partners.reduce((sum, p) => sum + (p.locations || 0), 0);
  const totalUsers = partners.reduce((sum, p) => sum + (p.activeUsers || 0), 0);
  const totalRedemptions = partners.reduce((sum, p) => sum + (p.totalRedemptions || 0), 0);
  return <div className="flex h-screen bg-gray-50" data-id="element-3376">
      <div className="w-20 flex-shrink-0 lg:block hidden" data-id="element-3377">
        {/* Placeholder for sidebar space on desktop, actual sidebar is fixed/relative in Sidebar component */}
      </div>
      <Sidebar onAddPartner={handleAddPartner} data-id="element-3378" />

      <div className="flex-1 flex flex-col overflow-hidden w-full" data-id="element-3379">
        <Topbar data-id="element-3380" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3381">
          {/* Page Header with AI Button */}
          <div className="mb-8" data-id="element-3382">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-3383">
              <div data-id="element-3384">
                <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3385">
                  Partners
                </h1>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-3386">
                  Gestisci i partner della piattaforma Greenback
                </p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-3387">
                <button onClick={handleAddPartner} className="flex-1 md:flex-none inline-flex justify-center items-center px-4 py-2 bg-greenback text-white text-sm font-medium hover:bg-greenback/90 transition-colors" style={{
                borderRadius: '100px'
              }} data-id="element-3388">
                  <Building2 className="h-4 w-4 mr-2" data-id="element-3389" />
                  Aggiungi Partner
                </button>
                <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-3390">
                  Suggerimenti AI
                </AIOutlineButton>
              </div>
            </div>
          </div>

          {/* Stats Overview - Changed rounded-lg to rounded-sm */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-3391">
            <div className="bg-white rounded-sm border border-gray-200 p-4" data-id="element-3392">
              <div className="flex items-center justify-between mb-2" data-id="element-3393">
                <Building2 className="h-5 w-5 text-gray-400" data-id="element-3394" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-3395">
                {partners.length}
              </p>
              <p className="text-sm font-normal text-gray-600" data-id="element-3396">
                Partner totali
              </p>
            </div>

            <div className="bg-white rounded-sm border border-gray-200 p-4" data-id="element-3397">
              <div className="flex items-center justify-between mb-2" data-id="element-3398">
                <MapPin className="h-5 w-5 text-gray-400" data-id="element-3399" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-3400">
                {totalLocations}
              </p>
              <p className="text-sm font-normal text-gray-600" data-id="element-3401">Sedi totali</p>
            </div>

            <div className="bg-white rounded-sm border border-gray-200 p-4" data-id="element-3402">
              <div className="flex items-center justify-between mb-2" data-id="element-3403">
                <Users className="h-5 w-5 text-gray-400" data-id="element-3404" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-3405">
                {totalUsers.toLocaleString('it-IT')}
              </p>
              <p className="text-sm font-normal text-gray-600" data-id="element-3406">Utenti attivi</p>
            </div>

            <div className="bg-white rounded-sm border border-gray-200 p-4" data-id="element-3407">
              <div className="flex items-center justify-between mb-2" data-id="element-3408">
                <Gift className="h-5 w-5 text-gray-400" data-id="element-3409" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-3410">
                {totalRedemptions.toLocaleString('it-IT')}
              </p>
              <p className="text-sm font-normal text-gray-600" data-id="element-3411">
                Riscatti totali
              </p>
            </div>
          </div>

          {/* AI Insights Section - Changed rounded-lg to rounded-sm */}
          {globalInsights.length > 0 && <div className="bg-white rounded-sm border border-gray-200 p-6 mb-6" data-id="element-3412">
              <div className="flex items-center gap-2 mb-4" data-id="element-3413">
                <Sparkles className="h-5 w-5 text-green-600" data-id="element-3414" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3415">
                  AI Insights Globali
                </h2>
              </div>
              <div className="space-y-3" data-id="element-3416">
                {globalInsights.filter(insight => insight.visible).map(insight => <div key={insight.id} className={`border rounded-sm p-4 ${getInsightBgColor(insight.type)}`} data-id="element-3417">
                      <div className="flex items-start justify-between mb-2" data-id="element-3418">
                        <div className="flex items-center gap-2" data-id="element-3419">
                          {getInsightIcon(insight.type)}
                          <h3 className="text-sm font-semibold text-gray-900" data-id="element-3420">
                            {insight.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-3421">
                          <button onClick={() => regenerateInsight(insight.id)} className="p-1 hover:bg-white/50 rounded transition-colors" title="Rigenera insight" data-id="element-3422">
                            <RefreshCw className="h-4 w-4 text-gray-600" data-id="element-3423" />
                          </button>
                          <button onClick={() => toggleInsightVisibility(insight.id)} className="p-1 hover:bg-white/50 rounded transition-colors" title="Nascondi insight" data-id="element-3424">
                            <EyeOff className="h-4 w-4 text-gray-600" data-id="element-3425" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm font-normal text-gray-700" data-id="element-3426">
                        {insight.description}
                      </p>
                    </div>)}
              </div>
            </div>}

          {/* Filters - Removed container, matching Users page style */}
          <div className="mb-6 flex flex-col md:flex-row flex-wrap gap-3" data-id="element-3427">
            {/* Search */}
            <div className="flex-1 min-w-full md:min-w-[300px] relative" data-id="element-3428">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3429" />
              <input type="text" placeholder="Cerca partner per nome, descrizione o categoria..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-sm text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-3430" />
            </div>

            <div className="flex flex-wrap gap-3 w-full md:w-auto" data-id="element-3431">
              {/* Category Filter */}
              <div className="flex-1 md:flex-none flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-3432">
                <Filter className="h-4 w-4 text-gray-500" data-id="element-3433" />
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="bg-transparent text-sm font-normal focus:outline-none text-gray-700 w-full md:w-auto" data-id="element-3434">
                  <option value="Tutte" data-id="element-3435">Tutte le categorie</option>
                  {allCategories.map(category => <option key={category} value={category} data-id="element-3436">
                      {category}
                    </option>)}
                </select>
              </div>

              {/* Status Filter */}
              <div className="flex-1 md:flex-none flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-3437">
                <Filter className="h-4 w-4 text-gray-500" data-id="element-3438" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-transparent text-sm font-normal focus:outline-none text-gray-700 w-full md:w-auto" data-id="element-3439">
                  <option value="all" data-id="element-3440">Tutti gli stati</option>
                  <option value="active" data-id="element-3441">Attivo</option>
                  <option value="inactive" data-id="element-3442">Inattivo</option>
                  <option value="pending" data-id="element-3443">In Attesa</option>
                </select>
              </div>

              {/* Export CSV Button - Tertiary style without outline */}
              <button onClick={handleExportCSV} className="flex-1 md:flex-none inline-flex justify-center items-center px-3 py-2 text-gray-700 text-sm font-normal hover:underline transition-all whitespace-nowrap" data-id="element-3444">
                <Download className="h-4 w-4 mr-2" data-id="element-3445" />
                Esporta CSV
              </button>
            </div>
          </div>

          {/* Partners Grid - Changed rounded-lg to rounded-sm */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3446">
            {filteredPartners.map(partner => <div key={partner.id} className="bg-white rounded-sm border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handlePartnerClick(partner.id)} data-id="element-3447">
                <div className="p-6" data-id="element-3448">
                  {/* Logo and Name */}
                  <div className="flex items-start gap-4 mb-4" data-id="element-3449">
                    <div className="h-16 w-16 rounded-sm border border-gray-200 bg-white flex items-center justify-center p-3 flex-shrink-0" data-id="element-3450">
                      <img src={partner.logo} alt={partner.name} className="max-h-full max-w-full object-contain" onError={e => {
                    e.currentTarget.src = 'https://eco-casa.es/wp-content/uploads/2014/04/logo_verds2.png';
                  }} data-id="element-3451" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-3452">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate" data-id="element-3453">
                        {partner.name}
                      </h3>
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700 transition-colors" data-id="element-3454">
                        <ExternalLink className="h-3 w-3" data-id="element-3455" />
                        <span className="truncate" data-id="element-3456">Visita sito</span>
                      </a>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm font-normal text-gray-600 mb-4 line-clamp-2" data-id="element-3457">
                    {partner.description}
                  </p>

                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mb-4" data-id="element-3458">
                    {partner.categories.slice(0, 3).map((category, index) => <span key={index} className="px-2 py-1 bg-green-600/10 text-green-600 text-xs font-medium rounded-full" data-id="element-3459">
                        {category}
                      </span>)}
                    {partner.categories.length > 3 && <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-3460">
                        +{partner.categories.length - 3}
                      </span>}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100" data-id="element-3461">
                    <div className="text-center" data-id="element-3462">
                      <p className="text-lg font-bold text-gray-900" data-id="element-3463">
                        {partner.locations}
                      </p>
                      <p className="text-xs font-normal text-gray-500" data-id="element-3464">Sedi</p>
                    </div>
                    <div className="text-center" data-id="element-3465">
                      <p className="text-lg font-bold text-gray-900" data-id="element-3466">
                        {partner.activeRewards}
                      </p>
                      <p className="text-xs font-normal text-gray-500" data-id="element-3467">Premi</p>
                    </div>
                    <div className="text-center" data-id="element-3468">
                      <p className="text-lg font-bold text-gray-900" data-id="element-3469">
                        {partner.totalRedemptions?.toLocaleString('it-IT')}
                      </p>
                      <p className="text-xs font-normal text-gray-500" data-id="element-3470">
                        Riscatti
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {/* No Results - Changed rounded-lg to rounded-sm */}
          {filteredPartners.length === 0 && <div className="bg-white rounded-sm border border-gray-200 p-12 text-center" data-id="element-3471">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-3472" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-3473">
                Nessun partner trovato
              </h3>
              <p className="text-sm font-normal text-gray-500 mb-4" data-id="element-3474">
                Prova a modificare i filtri di ricerca
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setSelectedCategory('Tutte');
            setStatusFilter('all');
          }} className="text-green-600 hover:underline text-sm font-medium" data-id="element-3475">
                Rimuovi tutti i filtri
              </button>
            </div>}
        </main>
      </div>

      {/* Bulk Email Modal - Changed rounded-lg to rounded-sm */}
      {showEmailModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-3476">
          <div className="bg-white rounded-sm max-w-2xl w-full p-6" data-id="element-3477">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3478">
              Invia Email a Tutti i Partner
            </h2>
            <p className="text-sm font-normal text-gray-600 mb-6" data-id="element-3479">
              Stai per inviare un'email a {filteredPartners.length} partner
              {selectedCategory !== 'Tutte' && ` nella categoria "${selectedCategory}"`}
              .
            </p>

            <div className="space-y-4 mb-6" data-id="element-3480">
              <div data-id="element-3481">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3482">
                  Oggetto
                </label>
                <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Inserisci l'oggetto dell'email..." className="w-full px-4 py-2 border border-gray-300 rounded-sm text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-3483" />
              </div>

              <div data-id="element-3484">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3485">
                  Messaggio
                </label>
                <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Scrivi il messaggio dell'email..." rows={6} className="w-full px-4 py-2 border border-gray-300 rounded-sm text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none" data-id="element-3486" />
              </div>
            </div>

            <div className="flex gap-3 justify-end" data-id="element-3487">
              <button onClick={() => {
            setShowEmailModal(false);
            setEmailSubject('');
            setEmailMessage('');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-sm text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-3488">
                Annulla
              </button>
              <button onClick={handleSendBulkEmail} disabled={!emailSubject || !emailMessage} className="px-4 py-2 bg-green-600 text-white rounded-sm text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3489">
                Invia Email
              </button>
            </div>
          </div>
        </div>}

      {/* Partner Create Drawer */}
      <PartnerCreateDrawer isOpen={showCreateDrawer} onClose={() => setShowCreateDrawer(false)} onSuccess={() => {
      console.log('Partner created successfully');
    }} data-id="element-3490" />

      {/* Partner Detail Drawer */}
      <PartnerDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} partnerId={selectedPartnerId} data-id="element-3491" />

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Partners" data-id="element-3492">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-3493" />
      </AISidebar>
    </div>;
};
export default Partners;